import { Group, Select } from '@byecode/ui'
import type { Field, IndependentAction } from '@lighthouse/core'
import type { CallActionConfig, FlowNode, NodeTypes, VariableTree2 } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getFormDatasourceOptions,
    getImageVariableOption,
    getInputInnerTypeMatch,
    getUpstreamActionsOptions,
    getUpstreamNodesOptions,
    getViewVariableOption,
    innerTypeToFieldTypeMapInCondition,
    ListItem4ByecodeUi,
    nodeTypeOptions,
    useAtomData,
    VariableSelect
} from '@lighthouse/shared'
import React, { useCallback, useMemo } from 'react'

import { dataSourcePoolAtom } from '@/atoms/dataSource/state'
import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useActionAdderDepParams } from '@/hooks/useActionAdderDepParams'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSource } from '@/hooks/useDataSource'
import { useIsDisabledWithVersion } from '@/hooks/useIsDisabledWithVersion'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'
import { useVariableCustomViewOption } from '@/hooks/useVariableCustomViewOption'

import * as SC from '../styles'

interface CallActionConfigureProps {
    nodeType?: NodeTypes
    value: CallActionConfig
    onChange: (value: CallActionConfig) => void
    allParentNodes?: FlowNode[]
    allParentActions?: IndependentAction[]
    actionTypeSwitcher?: React.ReactNode
}

const FIELD: Field = {
    id: '',
    dsId: '',
    type: 'phoneNumber',
    name: 'phoneNumber',
    innerType: 'TEXT'
}

export const CallActionConfigure: React.FC<CallActionConfigureProps> = ({
    nodeType,
    value,
    onChange,
    allParentNodes,
    allParentActions,
    actionTypeSwitcher
}) => {
    const dataSourceList = useAtomData(dataSourcePoolAtom)
    const disabledWithVersion = useIsDisabledWithVersion()
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const pageId = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => (isInFlow ? '' : s?.pageId || ''), [isInFlow])
    )

    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const isNotAutomation = type !== 'automation'

    const { viewDsId = '', formDsId = '', viewType, isImage } = useActionAdderDepParams()
    const formDataSource = useDataSource(appId, envId, formDsId)
    const viewDataSource = useDataSource(appId, envId, viewDsId)
    const workflowList = useAtomData(workflowListAtom)
    const formOption = useMemo(() => {
        if (formDataSource && !isInFlow) {
            return getFormDatasourceOptions({ dataSource: formDataSource })
        }
    }, [formDataSource, isInFlow])

    const { customViewOption } = useVariableCustomViewOption()

    const viewOptions = useMemo(() => {
        if (isInFlow) {
            return
        }

        const options: VariableTree2[] = []
        if (viewDataSource) {
            const viewOption = getViewVariableOption({ dataSource: viewDataSource, viewType })
            if (viewOption) {
                options.push(viewOption)

                return options
            }
        }
        if (customViewOption) {
            options.push(customViewOption)
        }
        return options
    }, [isInFlow, customViewOption, viewDataSource, viewType])

    const upstreamOptions = useMemo(() => {
        if (isInFlow) {
            return getUpstreamNodesOptions({
                workflowList,
                upstreamNodes: getAllCanBeUpstreamNodes(allParentNodes || []),
                allUpstreamNodes: allParentNodes,
                dataSourceList
            })
        }
        return getUpstreamActionsOptions({
            workflowList,
            allParentActions: allParentActions || [],
            dataSourceList
        })
    }, [allParentActions, allParentNodes, dataSourceList, isInFlow, workflowList])

    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const userOption = useGetUserRecordOptions()

    const imageOption = useMemo(() => {
        if (isImage) {
            return getImageVariableOption()
        }
    }, [isImage])

    return (
        <SC.Container>
            <Group label="动作配置">
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel>类型</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        {actionTypeSwitcher || <Select disabled value={nodeType} options={nodeTypeOptions} />}
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
                <ListItem4ByecodeUi>
                    <SC.FormItemLabelWrapper>
                        <SC.FormItemLabel required>电话号码</SC.FormItemLabel>
                    </SC.FormItemLabelWrapper>
                    <SC.FormItemContent>
                        <VariableSelect
                            field={FIELD}
                            innerTypes={getInputInnerTypeMatch(FIELD.innerType)}
                            disablePexels
                            disabled={disabledWithVersion}
                            viewOptions={viewOptions}
                            formOption={formOption}
                            pageOptions={isNotAutomation ? pageOptions : undefined}
                            upstreamOptions={upstreamOptions}
                            userOption={userOption}
                            imageOption={imageOption}
                            value={value.phone}
                            onChange={phone => onChange({ ...value, phone })}
                        />
                    </SC.FormItemContent>
                </ListItem4ByecodeUi>
            </Group>
        </SC.Container>
    )
}
